import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../api/client";
import { StatTile } from "../../components/shared/StatTile";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { InitialsAvatar } from "../../components/shared/InitialsAvatar";
import { DASHBOARD, ADMIN } from "../../constants/testIds";
import { useNavigate } from "react-router-dom";
import { Users, MapPin, Building2, FolderOpen, Radio, Flag, Compass, Clock } from "lucide-react";

export default function Dashboard() {
    const nav = useNavigate();
    const [users, setUsers] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [clients, setClients] = useState([]);
    const [live, setLive] = useState(null);
    const [variations, setVariations] = useState(null);

    useEffect(() => {
        (async () => {
            const [u, j, c] = await Promise.all([
                api.get("/users").then((r) => r.data),
                api.get("/jobs").then((r) => r.data),
                api.get("/clients").then((r) => r.data),
            ]);
            setUsers(u); setJobs(j); setClients(c);
        })();
    }, []);

    const loadLive = useCallback(async () => {
        try {
            const [l, v] = await Promise.all([
                api.get("/v1/admin/live").then((r) => r.data),
                api.get("/v1/admin/variations/today").then((r) => r.data),
            ]);
            setLive(l); setVariations(v);
        } catch (e) { /* silent */ }
    }, []);

    useEffect(() => {
        loadLive();
        const iv = setInterval(loadLive, 60_000);
        return () => clearInterval(iv);
    }, [loadLive]);

    const active = jobs.filter((j) => j.status === "active").length;
    const open = jobs.filter((j) => j.status === "open").length;

    return (
        <div data-testid={DASHBOARD.root} className="space-y-8">
            <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                    <div className="eyebrow">Admin</div>
                    <h1 className="font-extrabold tracking-[-0.03em] text-4xl leading-none">Dashboard</h1>
                    <p className="text-muted mt-2 text-sm">Live view · updates every 60 seconds · Brisbane time.</p>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted">
                    <Compass size={14} className="text-forest" strokeWidth={1.5} />
                    <span className="num">
                        {new Date().toLocaleString("en-AU", { timeZone: "Australia/Brisbane", dateStyle: "medium", timeStyle: "short" })}
                    </span>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatTile testid={DASHBOARD.tileUsers} label="Team" value={users.length}
                    hint={`${users.filter((u) => u.role === "staff").length} staff · ${users.filter((u) => u.role === "office").length} office`}
                    icon={Users} accent="var(--km-forest)" />
                <StatTile testid={DASHBOARD.tileJobsActive} label="Active jobs" value={active}
                    hint="on the ground now" icon={MapPin} accent="var(--km-accent)" />
                <StatTile testid={DASHBOARD.tileJobsOpen} label="Open" value={open}
                    hint="quoted, awaiting kickoff" icon={FolderOpen} accent="var(--km-amber)" />
                <StatTile testid={DASHBOARD.tileClients} label="Clients" value={clients.length}
                    hint="active accounts" icon={Building2} accent="var(--km-info)" />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                {/* Live tracking */}
                <div className="km-card p-6 xl:col-span-2" data-testid={ADMIN.livePanel}>
                    <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full bg-[color:var(--km-red)] animate-pulse" />
                            <h2 className="font-extrabold tracking-[-0.03em] text-xl">Live · who&apos;s on the clock</h2>
                            {live && <span className="km-chip km-chip-navy num">{live.counts.running}</span>}
                        </div>
                        <span className="text-xs text-muted">
                            {live?.today ? `today · ${live.today}` : ""}
                        </span>
                    </div>
                    {!live ? (
                        <div className="text-muted text-sm py-6">Fetching…</div>
                    ) : live.running.length === 0 && live.scheduled_not_started.length === 0 ? (
                        <div className="text-muted text-sm py-8 text-center">No one on the clock. Yard is quiet.</div>
                    ) : (
                        <div className="space-y-3">
                            {live.running.map((r) => (
                                <div key={r.entry_id} data-testid={ADMIN.liveRow(r.entry_id)} className="flex items-center gap-4 rounded-lg p-3" style={{ background: "var(--km-green-l)", border: "1px solid var(--km-green)" }}>
                                    <InitialsAvatar name={r.user?.name} initials={r.user?.initials} size={32} />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-ink font-medium text-sm">{r.user?.name || "Unknown"}</span>
                                            <span className="km-chip km-chip-green-solid">{r.work_type}</span>
                                        </div>
                                        <div className="text-xs text-muted truncate mt-0.5">
                                            {r.job ? (
                                                <>
                                                    <span className="num text-[color:var(--km-navy)] font-bold">{r.job.job_number || "—"}</span>
                                                    {" · "}{r.job.address}
                                                </>
                                            ) : (
                                                <span>{r.unallocated_address || "Unallocated"}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="num text-sm text-forest font-bold whitespace-nowrap flex items-center gap-1">
                                        <Clock size={13} /> {Math.floor(r.elapsed_min / 60)}h {r.elapsed_min % 60}m
                                    </div>
                                </div>
                            ))}
                            {live.scheduled_not_started.map((s, i) => (
                                <div key={i} className="flex items-center gap-4 rounded-lg p-3" style={{ background: "var(--surface-2)", border: "1px dashed var(--line-2)" }}>
                                    <InitialsAvatar name={s.user?.name} initials={s.user?.initials} size={32} />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-ink font-medium text-sm">{s.user?.name}</div>
                                        <div className="text-xs text-muted truncate">
                                            {s.job ? (
                                                <>
                                                    <span className="num text-[color:var(--km-navy)]">{s.job.job_number}</span>
                                                    {" · "}{s.job.address}
                                                </>
                                            ) : "Scheduled"}
                                        </div>
                                    </div>
                                    <span className="km-chip km-chip-grey text-[10px]">Not started</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Variations flagged today */}
                <div className="km-card p-6" data-testid={ADMIN.varsTodayPanel}>
                    <div className="flex items-baseline justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <Flag size={16} className="text-[color:var(--km-red)]" />
                            <h2 className="font-extrabold tracking-[-0.03em] text-xl">Variations today</h2>
                        </div>
                        {variations && <span className="km-chip km-chip-red num">{variations.variations.length}</span>}
                    </div>
                    {!variations ? (
                        <div className="text-muted text-sm py-6">Fetching…</div>
                    ) : variations.variations.length === 0 ? (
                        <div className="text-muted text-sm py-8 text-center">None flagged today. Good day so far.</div>
                    ) : (
                        <ul className="divide-y divide-km">
                            {variations.variations.slice(0, 6).map((v) => (
                                <li key={v.entry_id} className="py-3">
                                    <div className="flex items-start gap-3">
                                        <InitialsAvatar name={v.user?.name} initials={v.user?.initials} size={28} />
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm text-ink truncate">{v.user?.name}</div>
                                            {v.job && (
                                                <div className="text-xs text-muted truncate">
                                                    <span className="num text-[color:var(--km-navy)] font-bold">{v.job.job_number || "—"}</span>
                                                    {" · "}{v.job.address}
                                                </div>
                                            )}
                                            {v.note && (
                                                <div className="text-xs text-ink-2 mt-1 italic line-clamp-2">&ldquo;{v.note}&rdquo;</div>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                    {variations && variations.variations.length > 0 && (
                        <button
                            onClick={() => nav("/admin/approvals")}
                            className="text-xs text-[color:var(--km-navy)] hover:underline mt-3"
                        >
                            Resolve in approvals →
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
